import React, { useRef, MouseEvent, useEffect, useCallback } from 'react';
import * as Styled from './styled';
import { StyledText } from '@leasequery/lq.react-component-library';
import { LinkButton } from '../../../styles/components';
import { ReportSnapshot } from '../../../models';
import { FileTypes, isCustomAPReport } from '../../../util';
import Popover from '../../../components/popover/Popover';
import {
  PopoverController,
  PopoverPosition,
} from '../../../components/popover';
import { Icon } from '../../../components';
import { useDialogPropmt, DialogPrompt } from '../../../components/dialog';

interface ReportResultsTitleBarProps {
  reportSnapshot: ReportSnapshot;
  onExportExcelClicked?: (fileExtension: FileTypes) => void;
  onSendToInforSFTPClicked?: () => void;
  onSendToInforFSMClicked?: () => void;
  onSendToSFTPClicked?: () => void;
  isV11Report: boolean;
  isInforReport: boolean;
  isAPReport: boolean;
  isExportDisabled: boolean;
}

function ReportResultsTitleBar({
  reportSnapshot,
  onExportExcelClicked,
  onSendToInforSFTPClicked,
  onSendToInforFSMClicked,
  onSendToSFTPClicked,
  isV11Report,
  isInforReport,
  isAPReport,
  isExportDisabled
}: ReportResultsTitleBarProps): JSX.Element {
  const popoverSendToInforControllerRef = useRef(null as PopoverController);
  const onSendToInforActionsClicked = (event: MouseEvent): void => {
    popoverSendToInforControllerRef.current.showPopover(
      event.currentTarget as HTMLElement,
    );
  };

  const popoverOnControllerHandler = (controller: PopoverController): void => {
    popoverSendToInforControllerRef.current = controller;
  };

  const onSendToInforItemClicked = (callback: () => void): void => {
    popoverSendToInforControllerRef.current.hidePopover();
    callback();
  };

  const onSendToInforV10ItemClicked = (callback: () => void): void => {
    callback();
  };

  const scrollHandler = useCallback((): void => {
    popoverSendToInforControllerRef.current.reposition();
  }, [popoverSendToInforControllerRef]);

  const {
    showDialog: showConfirmFSMDialog,
    hideDialog: hideConfirmFSMDialog,
    disableDialog: disableConfirmFSMDialog,
    enableDialog: enableConfirmFSMDialog,
    ...confirmFSMDialogProps
  } = useDialogPropmt();

  const {
    showDialog: showConfirmDialog,
    hideDialog: hideConfirmDialog,
    disableDialog: disableConfirmDialog,
    enableDialog: enableConfirmDialog,
    ...confirmDialogProps
  } = useDialogPropmt();

  const confirmFSMDialogConfirmHandler = useCallback(async () => {
    disableConfirmFSMDialog();
    enableConfirmFSMDialog();
    hideConfirmFSMDialog();
    onSendToInforItemClicked(onSendToInforFSMClicked);
  }, [disableConfirmFSMDialog,
      enableConfirmFSMDialog,
      hideConfirmFSMDialog,
      onSendToInforFSMClicked]);

  const confirmDialogConfirmHandler = useCallback(async ()=> {
    disableConfirmDialog();
    enableConfirmDialog();
    hideConfirmDialog();
    if(isV11Report) {
      onSendToInforItemClicked(onSendToInforSFTPClicked);
    }
    else {
      onSendToInforV10ItemClicked(onSendToInforSFTPClicked);
    }

  }, [disableConfirmDialog,
    enableConfirmDialog,
    hideConfirmDialog,
    isV11Report,
    onSendToInforSFTPClicked]);

  useEffect(() => {
    if (isV11Report) {
      window.addEventListener('scroll', scrollHandler, true);
      return (): void => window.removeEventListener('scroll', scrollHandler);
    }
  }, [isV11Report, scrollHandler]);

  const onExportSFTPClicked = () => {
    disableConfirmDialog();
    enableConfirmDialog();
    hideConfirmDialog();
    onSendToSFTPClicked();
  };

  return (
    <Styled.ReportResultsTitleBar>
      <StyledText size="xl" font-weight="md">
        Report Results
      </StyledText>
      {reportSnapshot && (
        <>
          {isInforReport ? (
            <>
              <LinkButton disabled={isExportDisabled}
                onClick={(): void => onExportExcelClicked(FileTypes.csv)}
              >
                Export to CSV
              </LinkButton>
              {!isV11Report ? (
                <><LinkButton onClick={showConfirmDialog}>
                  Send to Infor
                </LinkButton>
                <DialogPrompt
                  {...confirmDialogProps}
                  title="CONFIRM"
                  labels={{
                    confirmButton: 'Yes',
                    cancelButton: 'No'
                  }}
                  onConfirm={confirmDialogConfirmHandler}
                >
                    Are you sure you want to send data from LeaseQuery?
                  </DialogPrompt></>
              ) : (
                <>
                  <LinkButton onClick={onSendToInforActionsClicked}>
                    Send to Infor &nbsp; <Icon name="dropdown" />
                  </LinkButton>
                  <Styled.SendToInforMenuWrapper>
                    <Popover
                      onController={popoverOnControllerHandler}
                      popoverPosition={PopoverPosition.Bottom}
                    >
                      <Styled.SendToInforItem onClick={showConfirmFSMDialog} >
                        <div>FSM</div>
                      </Styled.SendToInforItem>

                      <Styled.SendToInforItem onClick={showConfirmDialog} >
                        <div>SFTP</div>
                      </Styled.SendToInforItem>
                    </Popover>
                  </Styled.SendToInforMenuWrapper>
                  <DialogPrompt
                    {...confirmFSMDialogProps}
                    title="CONFIRM"
                    labels={{
                      confirmButton: 'Yes',
                      cancelButton: 'No'
                    }}
                    onConfirm={confirmFSMDialogConfirmHandler}
                  >
                    Are you sure you want to send data from LeaseQuery to FSM?
                </DialogPrompt>
                  <DialogPrompt
                    {...confirmDialogProps}
                    title="CONFIRM"
                    labels={{
                      confirmButton: 'Yes',
                      cancelButton: 'No'
                    }}
                    onConfirm={confirmDialogConfirmHandler}
                  >
                    Are you sure you want to send data from LeaseQuery?
                </DialogPrompt>
                </>
              )}
            </>
          ) : ( 
            <>
             { isCustomAPReport(reportSnapshot.reportName) ? (
              <LinkButton disabled={isExportDisabled}
                onClick={(): void => onExportExcelClicked(FileTypes.csv)}
              >
                Export to CSV
              </LinkButton>
              ) : (
              <LinkButton disabled={isExportDisabled}
                onClick={(): void => onExportExcelClicked(FileTypes.xlsx)}
              >
                Export to Excel
              </LinkButton>
              )}
            <>
            {isAPReport && (
                <>
                <LinkButton onClick={showConfirmDialog}>
                Export to SFTP
            </LinkButton>
            <DialogPrompt
                    {...confirmDialogProps}
                    title="CONFIRM"
                    labels={{
                      confirmButton: 'Yes',
                      cancelButton: 'No'
                    }}
                    onConfirm={onExportSFTPClicked}
                  >
                    Are you sure you want to do this?
                    </DialogPrompt>
                </>
            )}
            </>
            </>
          )}
        </>
      )}
    </Styled.ReportResultsTitleBar>
  );
}

export default ReportResultsTitleBar;
